/**
 * Geo-math foundation (M2). The ONE home for the great-circle + geodesic-circle +
 * bbox math shared across the app — the authalic-sphere constants, deg↔rad, the
 * haversine, the destination-point circle ring, and a point bounding box.
 *
 * geo/M2 is a FOUNDATION layer: it imports nothing from map/analysis/overlay/nl,
 * so every layer above can pull this math from ONE place without a layering
 * violation (the analysis radius tools, the routing provider's stub isochrone,
 * the NL spatial filter, the PointSet bounds, and the overlay tile bbox all draw
 * from here). Pure, dependency-free, and transferable to a Web Worker unchanged.
 *
 * NOTE (intentional non-consumers): the eval haversine controls in
 * `@/lib/eval/suites/geocode.ts` + `@/lib/eval/suites/nl.ts` deliberately keep an
 * INDEPENDENT duplicated formula — they are cross-checks against this code and
 * must not import it.
 */

/** A single [lng, lat] vertex — MapLibre/GeoJSON coordinate order. */
export type LngLat = [number, number];

/** Mean Earth radius (meters) — WGS84 authalic sphere, matches Turf's default. */
export const EARTH_RADIUS_M = 6_371_008.8;

/** Miles → meters (radius UI is mile-first; the spec stores meters). */
export const MILES_TO_METERS = 1609.344;

export const toRad = (deg: number): number => (deg * Math.PI) / 180;
export const toDeg = (rad: number): number => (rad * 180) / Math.PI;

/** Great-circle (haversine) distance between two [lng,lat] points, in meters. */
export function haversineMeters(a: LngLat, b: LngLat): number {
  const dLat = toRad(b[1] - a[1]);
  const dLng = toRad(b[0] - a[0]);
  const lat1 = toRad(a[1]);
  const lat2 = toRad(b[1]);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

/**
 * Approximate a geodesic circle as a closed polygon ring (`steps` vertices).
 * Destination-point formula on the authalic sphere, so the circle stays true at
 * any latitude (a naive dLng = dLat approximation skews badly far from the
 * equator). Matches the radius membership computed by {@link haversineMeters}.
 */
export function circleRing(center: LngLat, radiusMeters: number, steps = 64): LngLat[] {
  const [lng, lat] = center;
  const lat1 = toRad(lat);
  const lng1 = toRad(lng);
  const angular = radiusMeters / EARTH_RADIUS_M;
  const ring: LngLat[] = [];
  for (let i = 0; i <= steps; i++) {
    const bearing = (i / steps) * 2 * Math.PI;
    const lat2 = Math.asin(
      Math.sin(lat1) * Math.cos(angular) + Math.cos(lat1) * Math.sin(angular) * Math.cos(bearing),
    );
    const lng2 =
      lng1 +
      Math.atan2(
        Math.sin(bearing) * Math.sin(angular) * Math.cos(lat1),
        Math.cos(angular) - Math.sin(lat1) * Math.sin(lat2),
      );
    ring.push([toDeg(lng2), toDeg(lat2)]);
  }
  return ring;
}

/** A lng/lat bounding box (the PointSet bounds shape). */
export interface Bounds {
  minLng: number;
  minLat: number;
  maxLng: number;
  maxLat: number;
}

/** Bounding box of a set of {lng,lat} points, or null when empty. */
export function boundsOf(points: ReadonlyArray<{ lng: number; lat: number }>): Bounds | null {
  if (points.length === 0) return null;
  let minLng = Infinity;
  let minLat = Infinity;
  let maxLng = -Infinity;
  let maxLat = -Infinity;
  for (const p of points) {
    if (p.lng < minLng) minLng = p.lng;
    if (p.lat < minLat) minLat = p.lat;
    if (p.lng > maxLng) maxLng = p.lng;
    if (p.lat > maxLat) maxLat = p.lat;
  }
  return { minLng, minLat, maxLng, maxLat };
}
